// Production-optimized logger
// Reduces console spam and improves performance in production builds

const isProduction = process.env.NODE_ENV === "production";
const isWeb = typeof window !== "undefined";

// Rate limiting for frequent logs
const logRateLimit = new Map<string, number>();
const RATE_LIMIT_MS = 5000; // 5 seconds

export const ProductionLogger = {
  debug: (message: string, ...args: unknown[]) => {
    // Disable debug logs in production/web
    if (isProduction || isWeb) return;
    console.log(message, ...args);
  },

  info: (message: string, ...args: unknown[]) => {
    console.log(message, ...args);
  },

  warn: (message: string, ...args: unknown[]) => {
    console.warn(message, ...args);
  },

  error: (message: string, ...args: unknown[]) => {
    console.error(message, ...args);
  },

  // Rate-limited logging for frequently called functions
  rateLimited: (key: string, message: string, ...args: unknown[]) => {
    const now = Date.now();
    const lastLog = logRateLimit.get(key) || 0;

    if (now - lastLog > RATE_LIMIT_MS) {
      console.log(message, ...args);
      logRateLimit.set(key, now);
    }
  },

  // Performance logging (only in dev)
  performance: (message: string, duration?: number) => {
    if (isProduction || isWeb) return;
    if (duration !== undefined) {
      console.log(`⚡ ${message}: ${duration}ms`);
    } else {
      console.log(`⚡ ${message}`);
    }
  },
};

// Replace noisy console.log calls in production
if (isProduction || isWeb) {
  // Override the global console for production performance
  const originalLog = console.log;
  console.log = (...args: unknown[]) => {
    // Only log important messages, skip debug noise
    const message = args[0];
    if (typeof message === "string") {
      // Allow important messages
      if (
        message.includes("🚨") || // Errors
        message.includes("⚠️") || // Warnings
        message.includes("✅") || // Success
        message.includes("🚀") || // Important events
        message.includes("Error") ||
        message.includes("Failed")
      ) {
        originalLog(...args);
      }
      // Skip debug noise like "🎮", "📦", "🔐", etc.
    }
  };
}
